import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { faCheck, faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import SectionHeader from "./shared/SectionHeader";
import Text from "./shared/Text";

/**
 * PricingPlansCompareSection — "Compare Plans" block for the V2 pricing
 * page. Renders a centered eyebrow/title/subtitle header followed by a
 * row of 3 plan cards (Launch / Grow / Rise). The middle card is
 * visually lifted with a "Recommended" ribbon.
 *
 * Below the plan grid lives a secondary "Need more?" row — a stacked
 * light copy block on the left and a black Unlimited card on the right,
 * for larger teams / custom pricing.
 */

export type PricingPlanFeature = {
  label: string;
  icon?: "check" | "sparkles";
};

export type PricingPlanCard = {
  name: string;
  tagline: string;
  price: string;
  pricePeriod: string;
  annualPrice?: string;
  annualPricePeriod?: string;
  feeLine: string;
  description: string;
  cta: CtaType;
  featuresHeading: string;
  features: PricingPlanFeature[];
  recommended?: boolean;
};

export type PricingNeedMoreCard = {
  name: string;
  tagline: string;
  cta: CtaType;
  features: string[];
};

export type PricingPlansCompareData = {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  showToggle?: boolean;
  plans: [PricingPlanCard, PricingPlanCard, PricingPlanCard];
  needMore?: {
    heading: string;
    description: string;
    card: PricingNeedMoreCard;
  };
};

const BulletIcon = ({ variant }: { variant: "check" | "sparkles" }) => (
  <span
    aria-hidden="true"
    className={
      variant === "sparkles"
        ? "inline-flex h-5 w-5 shrink-0 items-center justify-center text-[#5766F2]"
        : "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#5766F2]"
    }
  >
    <FontAwesomeIcon
      icon={variant === "sparkles" ? faSparkles : faCheck}
      className={
        variant === "sparkles"
          ? "h-3.5 w-3.5 text-[#5766F2]"
          : "h-2.5 w-2.5 text-[#5766F2]"
      }
    />
  </span>
);

const PlanCard = ({ plan, isAnnual }: { plan: PricingPlanCard; isAnnual?: boolean }) => {
  const displayPrice = isAnnual && plan.annualPrice ? plan.annualPrice : plan.price;
  const displayPeriod = isAnnual && plan.annualPricePeriod ? plan.annualPricePeriod : plan.pricePeriod;
  const body = (
    <div
      className={`flex h-full flex-col rounded-3xl border border-[#E5E5E5] ${
        plan.recommended ? "bg-[#F7F9FA]" : "bg-white"
      } overflow-hidden`}
    >
      {/* Recommended banner or equal-height spacer — keeps content aligned across all cards */}
      {plan.recommended ? (
        <div className="flex h-9 w-full items-center justify-center bg-[#E8EAFF]">
          <Text
            as="span"
            variant="body-md"
            className="text-[11px] font-semibold tracking-wide text-[#5766F2]"
          >
            Recommended
          </Text>
        </div>
      ) : (
        <div className="h-9" />
      )}
      {/* Top block — name, price, description, CTA */}
      <div className="flex flex-col px-7 pb-7 pt-8 md:px-8 md:pb-8 md:pt-10">
        <Text
          as="h3"
          variant="title-xl"
          className="!text-[24px] font-medium text-neutralBlack"
        >
          {plan.name}
        </Text>
        <Text
          as="p"
          variant="body-md"
          className="mt-1 text-[14px] text-[#6B7280] md:text-[15px]"
        >
          {plan.tagline}
        </Text>

        <div className="mt-6 flex items-baseline gap-1">
          <Text
            as="span"
            variant="display-lg"
            className="!text-[36px] font-bold text-neutralBlack"
          >
            {displayPrice}
          </Text>
          <Text
            as="span"
            variant="body-sm"
            className="text-[12px] text-neutralBlack md:text-[13px]"
          >
            {displayPeriod}
          </Text>
        </div>

        <Text
          as="p"
          variant="body-md"
          className="mt-4 text-[13px] leading-snug text-neutralBlack md:text-[14px]"
        >
          {plan.feeLine}
        </Text>

        <Text
          as="p"
          variant="body-md"
          className="mt-5 min-h-[68px] text-[13px] leading-relaxed text-[#6B7280] md:text-[14px]"
        >
          {plan.description}
        </Text>

        <div className="mt-6">
          <CtaButtonGroup
            ctas={[plan.cta]}
            gap="flex w-full"
            fullWidthOnMobile={true}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="h-px w-full bg-[#E5E5E5]" />

      {/* Features list */}
      <div className="flex-1 px-7 pb-8 pt-6 md:px-8 md:pb-10 md:pt-7">
        <Text
          as="p"
          variant="body-md"
          className="text-[14px] font-semibold text-neutralBlack md:text-[15px]"
        >
          {plan.featuresHeading}
        </Text>
        <ul className="mt-4 flex flex-col gap-3">
          {plan.features.map((f) => (
            <li key={f.label} className="flex items-start gap-3">
              <BulletIcon variant={f.icon ?? "check"} />
              <Text
                as="span"
                variant="body-md"
                className="text-[13px] leading-snug text-neutralBlack md:text-[14px]"
              >
                {f.label}
              </Text>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return body;
};

const NeedMoreRow = ({
  data,
}: {
  data: NonNullable<PricingPlansCompareData["needMore"]>;
}) => {
  return (
    <div className="relative mt-14 md:mt-20">
      {/* Soft peach + lavender background wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 -top-16 -left-[50vw] -right-[50vw] -z-10"
        style={{
          background:
            "radial-gradient(ellipse 55% 80% at 0% 50%, rgba(230, 210, 255, 0.30) 0%, rgba(230, 210, 255, 0) 70%)," +
            "radial-gradient(ellipse 55% 80% at 100% 50%, rgba(255, 220, 200, 0.30) 0%, rgba(255, 220, 200, 0) 70%)",
        }}
      />

      <div className="mx-auto max-w-6xl grid grid-cols-1 items-center gap-8 py-10 md:grid-cols-[1fr_1.4fr] md:gap-10 md:py-14 lg:gap-14">
        {/* LEFT copy */}
        <div>
          <Text
            as="h3"
            variant="title-xl"
            className="text-[22px] font-medium text-neutralBlack md:text-[26px]"
          >
            {data.heading}
          </Text>
          <Text
            as="p"
            variant="body-md"
            className="mt-3 max-w-sm text-[14px] leading-relaxed text-neutralBlack md:text-[15px]"
          >
            {data.description}
          </Text>
        </div>

        {/* RIGHT black card */}
        <div className="rounded-3xl bg-neutralBlack p-6 text-white md:p-8">
          <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 md:gap-8">
            {/* Name block + CTA */}
            <div>
              <Text
                as="h4"
                variant="title-xl"
                className="text-[22px] font-bold text-white md:text-[26px]"
              >
                {data.card.name}
              </Text>
              <Text
                as="p"
                variant="body-md"
                className="mt-2 text-[13px] text-white/70 md:text-[14px]"
              >
                {data.card.tagline}
              </Text>
              <div className="mt-5">
                <CtaButtonGroup
                  ctas={[data.card.cta]}
                  gap="flex"
                  fullWidthOnMobile={false}
                />
              </div>
            </div>

            {/* Feature bullets */}
            <ul className="flex flex-col gap-3">
              {data.card.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-white/30"
                  >
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="h-2.5 w-2.5 text-white"
                    />
                  </span>
                  <Text
                    as="span"
                    variant="body-md"
                    className="text-[13px] font-normal leading-snug text-white/80 md:text-[14px]"
                  >
                    {f}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const PricingPlansCompareSection = ({
  eyebrow = "PRICING",
  title,
  subtitle,
  showToggle,
  plans,
  needMore,
}: PricingPlansCompareData) => {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section className="relative w-full overflow-hidden bg-white">
      {/* Peach / orange / yellow gradient wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 0% 100%, rgba(255, 200, 150, 0.30) 0%, rgba(255, 255, 255, 0) 65%)," +
            "radial-gradient(ellipse 80% 60% at 100% 0%, rgba(255, 230, 180, 0.25) 0%, rgba(255, 255, 255, 0) 65%)," +
            "radial-gradient(ellipse 60% 50% at 50% 100%, rgba(255, 180, 120, 0.15) 0%, rgba(255, 255, 255, 0) 70%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
        }}
      />
      <div className="container relative z-10 mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          variant="light-olive"
          align="center"
        />

        {showToggle && (
          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center rounded-full border border-[#E5E5E5] bg-[#F7F9FA] p-1">
              <button
                onClick={() => setIsAnnual(false)}
                className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${
                  !isAnnual
                    ? "bg-white text-neutralBlack shadow-sm"
                    : "text-[#6B7280]"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`flex items-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all ${
                  isAnnual
                    ? "bg-white text-neutralBlack shadow-sm"
                    : "text-[#6B7280]"
                }`}
              >
                Annual
                <span className="rounded-full bg-[#E8EAFF] px-2 py-0.5 text-xs font-semibold text-[#5766F2]">
                  Save 25%
                </span>
              </button>
            </div>
          </div>
        )}

        <div className="mx-auto mt-12 grid max-w-6xl grid-cols-1 items-stretch gap-6 md:mt-16 md:grid-cols-3 md:gap-5 lg:gap-6">
          {plans.map((plan) => (
            <PlanCard key={plan.name} plan={plan} isAnnual={isAnnual} />
          ))}
        </div>

        {needMore ? <NeedMoreRow data={needMore} /> : null}
      </div>
    </section>
  );
};

export default PricingPlansCompareSection;
